export type DriveRole = 'ADMIN' | 'EDITOR' | 'VIEWER';
export type WorkspaceRole = 'admin' | 'member';
export type Theme = 'light' | 'dark';
export type ViewMode = 'workspace' | 'personal';
export type DriveView =
  | 'browse'
  | 'recent'
  | 'shared'
  | 'starred'
  | 'trash'
  | 'requests'
  | 'transfers'
  | 'storage'
  | 'account'
  | 'members'
  | 'storagereq'
  | 'admin';

export interface Space {
  id: string;
  type: 'personal' | 'team';
  name: string;
  tenant_id: string;
  owner_user_id?: string;
  state: 'active' | 'archived';
  created_at: string;
}

export interface Folder {
  id: string;
  space_id: string;
  parent_folder_id?: string;
  name: string;
  tenant_id: string;
  created_at: string;
  _count?: { items: number; subfolders: number };
}

export interface FileVersion {
  id: string;
  item_id: string;
  version_no: number;
  file_id: string;
  size_bytes: number;
  is_current: boolean;
  created_by: string;
  created_at: string;
}

// The four sharing tiers, in ascending order of what they allow. 'commenter'
// sits between viewer and editor — can view + comment, cannot edit contents.
export type SharePermission = 'viewer' | 'commenter' | 'editor' | 'manager';

// Mirrors the RANK lattice in sk-drive-api's access.ts — kept here rather than
// duplicated per-component so every permission check in the UI agrees.
export const PERMISSION_RANK: Record<SharePermission, number> = { viewer: 1, commenter: 2, editor: 3, manager: 4 };
export const hasPermissionAtLeast = (have: SharePermission | undefined, need: SharePermission): boolean =>
  !!have && PERMISSION_RANK[have] >= PERMISSION_RANK[need];

export interface ShareGrant {
  id: string;
  target_type: 'folder' | 'item';
  folder_id?: string | null;
  item_id?: string | null;
  grantee_type: 'user' | 'group';
  grantee_id: string;
  permission: SharePermission;
  granted_by: string;
  created_at: string;
}

// Matches the API's actual response shape (app/links.ts `present()`) — field
// names here previously didn't match the backend at all (access/permission:
// 'view'|'edit'/allow_downloads), which silently broke link creation.
export interface SharedLink {
  id: string;
  name?: string | null;
  token: string;
  scope: 'team' | 'anyone';
  permission: SharePermission;
  allow_download: boolean;
  allow_print: boolean;
  password_protected: boolean;
  expires_at?: string | null;
  view_count: string;
  download_count: string;
  state: 'active' | 'deactivated';
  created_by: string;
  created_at: string;
}

export interface LinkAccess {
  guest_label: string;
  views: string;
  downloads: string;
  first_seen_at: string;
  last_seen_at: string;
}

export interface LinkStats {
  external_users: number;
  views: string;
  downloads: string;
  accesses: LinkAccess[];
}

// A request to raise (or lower) one's own permission tier on an item/folder —
// mirrors app/permissionRequests.ts's drive_permission_request shape.
export interface PermissionRequest {
  id: string;
  target_type: 'folder' | 'item';
  folder_id?: string | null;
  item_id?: string | null;
  requested_by: string;
  current_permission?: SharePermission | null;
  requested_permission: SharePermission;
  reason?: string | null;
  status: 'pending' | 'approved' | 'denied';
  decided_by?: string | null;
  decided_at?: string | null;
  created_at: string;
}

export interface Comment {
  id: string;
  body: string;
  author_id: string;
  author_name?: string;
  resolved: boolean;
  created_at: string;
}

export interface DriveItem {
  id: string;
  tenant_id: string;
  space_id: string;
  folder_id?: string;
  name: string;
  owner_user_id: string;
  mime_category: 'image' | 'video' | 'audio' | 'text' | 'application';
  state: 'active' | 'trashed' | 'purged';
  hold_active: boolean;
  hold_reason?: string;
  starred: boolean;
  availability: 'online_only' | 'offline';
  scan_status?: 'pending' | 'scanning' | 'clean' | 'infected';
  current_version_id?: string;
  created_at: string;
  updated_at: string;
  trashed_at?: string;
  // Relations (may be populated)
  currentVersion?: FileVersion;
  link?: SharedLink;
  tags?: Tag[];
  shares?: ShareGrant[];
  comments?: Comment[];
  versions?: FileVersion[];
  // Top-level from API (computed from current_version)
  size_bytes?: string | null;
  last_accessed_at?: string | null;
  has_thumbnail?: boolean;
  // Only present on the single-item fetch (GET /items/:id), not list
  // responses — the caller's own effective permission on this item.
  my_permission?: SharePermission;
  // Computed / UI
  ext?: string;
  previewUrl?: string;
  sha?: string;
  lastUsed?: string;
}

export interface Tag {
  id: string;
  name: string;
  color?: string;
}

// An org colleague eligible to be added to the "People & groups" sharing
// picker — sourced from sk-people via the backbone People gateway.
export interface Person {
  id: string;
  name: string;
  email?: string;
}

export interface Transfer {
  id: string;
  name: string;
  token: string;
  state: 'active' | 'expired' | 'revoked';
  password_protected: boolean;
  expires_at: string;
  created_at: string;
  file_count: number;
  // BigInt-backed values arrive as strings (see the API's serialize util).
  total_bytes: string;
  view_count: string;
  download_count: string;
}

export interface FileRequest {
  id: string;
  title: string;
  token: string;
  status: 'open' | 'closed';
  space_id: string;
  folder_id?: string | null;
  folder_name?: string | null;
  deadline?: string | null;
  received_count: string;
  created_at: string;
}

// Public, anonymous payloads (no session) — served from the API root, not /api/drive/v1.
export interface PublicTransfer {
  name: string;
  expires_at: string;
  total_bytes: string;
  files: Array<{ file_id: string; mime_type: string; size_bytes: string; filename: string | null }>;
}

export interface PublicFileRequest {
  title: string;
  status: 'open' | 'closed';
  deadline: string | null;
  received_count: string;
}

export interface WorkspaceMember {
  id: string;
  user_id: string;
  role: WorkspaceRole;
  status: 'active' | 'invited' | 'suspended';
  quota_bytes: string;
  joined_at: string;
  // Joined from platform
  name?: string;
  email?: string;
  used_bytes?: number;
}

export interface StorageRequest {
  id: string;
  user_id: string;
  amount_bytes: number;
  reason: string;
  status: 'pending' | 'approved' | 'denied';
  created_at: string;
  resolved_at?: string;
  member?: WorkspaceMember;
}

export interface Notification {
  id: string;
  type: string;
  actor?: string;
  text: string;
  read: boolean;
  created_at: string;
}

export interface UsageSummary {
  used_bytes: number;
  quota_bytes: number;
  file_count: number;
}

export interface PlanInfo {
  id: string;
  name: string;
  storage_gb: number;
  price_monthly: number;
}

export interface BreadcrumbItem {
  id: string;
  name: string;
  type: 'space' | 'folder';
}

// A global-search result: an item plus where it lives, so the hit is locatable
// even though it may be in a space the user isn't currently browsing.
export interface SearchHit extends DriveItem {
  path: BreadcrumbItem[];
}
